import { useRef, useState, type DragEvent } from 'react';
import {
  BUILDER_KINDS, BUILDER_ORDER, convertItem, itemProblem, newItem, tilesOf,
  type BuilderItem, type BuilderKind,
} from '../lib/grammar/builder';

// ─────────────────────────────────────────────────────────────────────────────
// The Custom Lesson Builder. Drag a question type from the palette into the
// lesson (or click it to add at the end), then drag cards by their handle to
// reorder. Students get the questions in exactly this order.
// Touch screens don't do HTML drag and drop, so every card also has ↑ ↓.
// ─────────────────────────────────────────────────────────────────────────────

type Examples = { prompt: Record<BuilderKind, string>; answer: Record<BuilderKind, string>; wrong: string[]; extra: string; why: string };
const EXAMPLES: Record<'es' | 'en', Examples> = {
  es: {
    prompt: { choice: 'Mi hermana ___ muy alta.', blank: 'Yo ___ estudiante.', short: '¿Cuál es la capital de España?', truefalse: '“Estoy cansado” uses ser.', translate: 'Me gusta leer.', order: 'I like to read books.' },
    answer: { choice: 'es', blank: 'soy', short: 'Madrid', truefalse: '', translate: 'I like to read. / I like reading.', order: 'Me gusta leer libros.' },
    wrong: ['está', 'son', 'eres'], extra: 'leo libro', why: 'Tired is a state → estar',
  },
  en: {
    prompt: { choice: 'She ___ to school yesterday.', blank: 'I ___ a student.', short: 'What is the past of “go”?', truefalse: '“Informations” is correct English.', translate: 'Je suis fatigué.', order: 'Clue: a question about the time' },
    answer: { choice: 'went', blank: 'am', short: 'went', truefalse: '', translate: 'I am tired. / I’m tired.', order: 'What time is it?' },
    wrong: ['goes', 'gone', 'going'], extra: 'does are', why: 'Yesterday → past simple',
  },
};

const NEW_KIND = 'application/x-gbf-kind';
const MOVE_ID = 'application/x-gbf-item';

type Props = {
  items: BuilderItem[];
  /** The class's language: Spanish classes get Spanish examples, others English ones. */
  language: string;
  onChange: (items: BuilderItem[]) => void;
};

export default function LessonBuilder({ items, language, onChange }: Props) {
  const ex = language === 'es' ? EXAMPLES.es : EXAMPLES.en;
  const listRef = useRef<HTMLDivElement>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);   // the card just added, to focus

  const update = (id: string, patch: Partial<BuilderItem>) =>
    onChange(items.map(it => (it.id === id ? { ...it, ...patch } : it)));
  const remove = (id: string) => onChange(items.filter(it => it.id !== id));
  const move = (from: number, to: number) => {
    if (to < 0 || to > items.length || from === to || from + 1 === to) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to > from ? to - 1 : to, 0, it);
    onChange(next);
  };
  const insert = (kind: BuilderKind, at = items.length) => {
    const it = newItem(kind);
    onChange([...items.slice(0, at), it, ...items.slice(at)]);
    setFresh(it.id);
  };

  /** Where in the list the pointer is: before the first card whose middle is below it. */
  const indexAt = (y: number) => {
    const cards = [...(listRef.current?.querySelectorAll<HTMLElement>(':scope > .qcard') ?? [])];
    const i = cards.findIndex(c => { const r = c.getBoundingClientRect(); return y < r.top + r.height / 2; });
    return i < 0 ? cards.length : i;
  };
  const onDragOver = (e: DragEvent) => {
    const types = e.dataTransfer.types;
    if (!types.includes(NEW_KIND) && !types.includes(MOVE_ID)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = types.includes(MOVE_ID) ? 'move' : 'copy';
    setDropAt(indexAt(e.clientY));
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const at = indexAt(e.clientY);
    const kind = e.dataTransfer.getData(NEW_KIND) as BuilderKind;
    const id = e.dataTransfer.getData(MOVE_ID);
    if (kind && BUILDER_KINDS[kind]) insert(kind, at);
    else if (id) move(items.findIndex(it => it.id === id), at);
    setDropAt(null); setDragging(null);
  };

  const ready = items.filter(it => !itemProblem(it)).length;

  return (
    <div className="builder">
      <div className="builder-palette" aria-label="Question types">
        {BUILDER_ORDER.map(k => (
          <button
            type="button" key={k} className="palette-item" draggable
            onDragStart={e => { e.dataTransfer.setData(NEW_KIND, k); e.dataTransfer.effectAllowed = 'copy'; }}
            onDragEnd={() => setDropAt(null)}
            onClick={() => insert(k)}
            title={`${BUILDER_KINDS[k].blurb}. Drag into the lesson, or click to add at the end.`}
          >
            <span className="palette-icon" aria-hidden>{BUILDER_KINDS[k].icon}</span>
            <span className="grow"><strong>{BUILDER_KINDS[k].name}</strong><span className="small muted">{BUILDER_KINDS[k].blurb}</span></span>
            <span className="palette-add" aria-hidden>＋</span>
          </button>
        ))}
      </div>

      <div
        ref={listRef} className={`builder-list${items.length ? '' : ' empty'}${dropAt !== null ? ' over' : ''}`}
        onDragOver={onDragOver} onDrop={onDrop}
        onDragLeave={e => { if (!listRef.current?.contains(e.relatedTarget as Node)) setDropAt(null); }}
      >
        {items.length === 0 && (
          <div className="builder-empty">
            <strong>Drag a question type here</strong>
            <span className="small muted">or click one above to add it at the end.</span>
          </div>
        )}
        {items.map((it, i) => (
          <QuestionCard
            key={it.id} item={it} n={i + 1} total={items.length} ex={ex}
            dropBefore={dropAt === i} dragging={dragging === it.id} autoFocus={fresh === it.id}
            onDragStart={e => {
              e.dataTransfer.setData(MOVE_ID, it.id);
              e.dataTransfer.effectAllowed = 'move';
              // Drag the whole card, not just the handle.
              const card = (e.currentTarget as HTMLElement).closest('.qcard');
              if (card) e.dataTransfer.setDragImage(card, 24, 24);
              setDragging(it.id);
            }}
            onDragEnd={() => { setDragging(null); setDropAt(null); }}
            onChange={patch => update(it.id, patch)}
            onKind={k => onChange(items.map(x => (x.id === it.id ? convertItem(x, k) : x)))}
            onRemove={() => remove(it.id)}
            onUp={() => move(i, i - 1)}
            onDown={() => move(i, i + 2)}
          />
        ))}
        {dropAt === items.length && items.length > 0 && <div className="drop-line" />}
      </div>

      <div className="builder-foot small">
        {items.length === 0 ? null : ready < items.length
          ? <span className="warn-text">{items.length - ready} question{items.length - ready === 1 ? '' : 's'} not finished yet, and won't be in the lesson.</span>
          : <span className="muted">✓ {ready} question{ready === 1 ? '' : 's'} ready.</span>}
        {ready > 0 && ready < 5 && <span className="warn-text"> A lesson needs at least 5 questions to count.</span>}
      </div>
    </div>
  );
}

function QuestionCard({ item, n, total, ex, dropBefore, dragging, autoFocus, onDragStart, onDragEnd, onChange, onKind, onRemove, onUp, onDown }: {
  item: BuilderItem; n: number; total: number; ex: Examples; dropBefore: boolean; dragging: boolean; autoFocus: boolean;
  onDragStart: (e: DragEvent) => void; onDragEnd: () => void;
  onChange: (patch: Partial<BuilderItem>) => void; onKind: (k: BuilderKind) => void;
  onRemove: () => void; onUp: () => void; onDown: () => void;
}) {
  const promptRef = useRef<HTMLInputElement>(null);
  const problem = itemProblem(item);
  const id = `q-${item.id}`;
  const k = item.kind;

  const insertBlank = () => {
    const el = promptRef.current;
    const at = el?.selectionStart ?? item.prompt.length;
    const p = item.prompt;
    const gap = (s: string, i: number) => (i > 0 && s[i - 1] !== ' ' ? ' ' : '');
    onChange({ prompt: `${p.slice(0, at)}${gap(p, at)}___${p[at] && p[at] !== ' ' ? ' ' : ''}${p.slice(at)}` });
    requestAnimationFrame(() => el?.focus());
  };

  const promptLabel = {
    choice: 'Question', blank: 'Sentence', short: 'Question', truefalse: 'Statement',
    translate: 'Sentence to translate', order: 'Clue (optional)',
  }[k];
  const promptHolder = ex.prompt[k];

  return (
    <>
      {dropBefore && <div className="drop-line" />}
      <div className={`qcard${dragging ? ' dragging' : ''}${problem ? ' unfinished' : ''}`}>
        <div className="qcard-head">
          <span className="drag-handle" draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
            title="Drag to reorder" aria-hidden>⋮⋮</span>
          <span className="qcard-n">{n}</span>
          <select value={k} onChange={e => onKind(e.target.value as BuilderKind)} aria-label={`Question ${n} type`}>
            {BUILDER_ORDER.map(x => <option key={x} value={x}>{BUILDER_KINDS[x].icon} {BUILDER_KINDS[x].name}</option>)}
          </select>
          <span className="grow" />
          <button type="button" className="icon" onClick={onUp} disabled={n === 1} aria-label={`Move question ${n} up`}>↑</button>
          <button type="button" className="icon" onClick={onDown} disabled={n === total} aria-label={`Move question ${n} down`}>↓</button>
          <button type="button" className="icon" onClick={onRemove} aria-label={`Delete question ${n}`}>🗑</button>
        </div>

        <div className="qcard-body">
          <label htmlFor={id}>{promptLabel}</label>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input id={id} ref={promptRef} dir="auto" value={item.prompt} autoFocus={autoFocus}
              onChange={e => onChange({ prompt: e.target.value })} placeholder={promptHolder} />
            {(k === 'blank' || k === 'choice') && (
              <button type="button" className="small" onClick={insertBlank} title="Insert a blank where the cursor is">___</button>
            )}
          </div>

          {k === 'truefalse' ? (
            <div className="tf-pick" role="radiogroup" aria-label="Answer">
              {(['true', 'false'] as const).map(v => (
                <button type="button" key={v} role="radio" aria-checked={item.answer === v}
                  className={item.answer === v ? 'on' : ''} onClick={() => onChange({ answer: v })}>
                  {v === 'true' ? '✓ True' : '✗ False'}
                </button>
              ))}
            </div>
          ) : (
            <>
              <label htmlFor={`${id}-a`}>
                {k === 'choice' ? 'Right answer' : k === 'order' ? 'The sentence, in the right order' : k === 'translate' ? 'Translation' : 'Answer'}
              </label>
              <input id={`${id}-a`} dir="auto" value={item.answer} onChange={e => onChange({ answer: e.target.value })}
                placeholder={ex.answer[k]} />
              {(k === 'blank' || k === 'short' || k === 'translate') && (
                <span className="hint">More than one right answer? Separate them with / (e.g. <code>soy / estoy</code>).</span>
              )}
            </>
          )}

          {k === 'choice' && (
            <>
              <label>Wrong answers</label>
              <div className="wrong-list">
                {(item.wrong ?? []).map((w, j) => (
                  <div key={j} className="row" style={{ flexWrap: 'nowrap' }}>
                    <input dir="auto" value={w} aria-label={`Wrong answer ${j + 1}`} placeholder={ex.wrong[j] ?? ''}
                      onChange={e => onChange({ wrong: (item.wrong ?? []).map((x, i) => (i === j ? e.target.value : x)) })} />
                    {(item.wrong ?? []).length > 1 && (
                      <button type="button" className="icon" aria-label={`Remove wrong answer ${j + 1}`}
                        onClick={() => onChange({ wrong: (item.wrong ?? []).filter((_, i) => i !== j) })}>✕</button>
                    )}
                  </div>
                ))}
                {(item.wrong ?? []).length < 3 && (
                  <button type="button" className="small link-button" onClick={() => onChange({ wrong: [...(item.wrong ?? []), ''] })}>
                    ＋ Add a wrong answer
                  </button>
                )}
              </div>
            </>
          )}

          {k === 'order' && (
            <>
              <label htmlFor={`${id}-x`}>Extra words to make it harder (optional)</label>
              <input id={`${id}-x`} dir="auto" value={item.extra ?? ''} onChange={e => onChange({ extra: e.target.value })}
                placeholder={ex.extra} />
              {tilesOf(item.answer).length > 1 && (
                <div className="tile-preview" dir="auto" aria-label="Students see these tiles">
                  {[...tilesOf(item.answer), ...tilesOf(item.extra ?? '')].map((t, i) => <span key={i} className="tile">{t}</span>)}
                </div>
              )}
            </>
          )}

          <details className="why-field" open={!!item.why}>
            <summary className="small muted">Explanation shown after answering (optional)</summary>
            <input dir="auto" value={item.why ?? ''} onChange={e => onChange({ why: e.target.value })}
              placeholder={ex.why} aria-label="Explanation" />
          </details>

          {problem && <span className="hint warn">{problem}</span>}
        </div>
      </div>
    </>
  );
}
